import { Component, SimpleChanges, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { addDays } from '@progress/kendo-date-math';
import { FilterBase } from 'src/app/shared/grid/filter-base';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from '@progress/kendo-angular-dateinputs';

@UntilDestroy()
@Component({
    selector: 'grid-date-range-filter',
    template: ` <div class="k-form">
        <label class="k-form-field">
            <span>{{ labelAfter }}</span>
        </label>
        <kendo-datepicker
            class="nova-kendo-datepicker"
            (valueChange)="onStartChange($event)"
            format="MM/dd/yyyy"
            [(ngModel)]="start"
            [max]="max"></kendo-datepicker>
        <label class="k-form-field">
            <span>{{ labelBefore }}</span>
        </label>
        <kendo-datepicker
            class="nova-kendo-datepicker"
            (valueChange)="onEndChange($event)"
            format="MM/dd/yyyy"
            [(ngModel)]="end"
            [min]="min"></kendo-datepicker>
    </div>`,
    providers: [{ provide: FilterBase, useExisting: GridDateRangeFilterComponent }],
    styles: [
        `
            .k-form {
                padding: 5px;
            }
        `
    ],
    standalone: true,
    imports: [FormsModule, DatePickerModule]
})
export class GridDateRangeFilterComponent extends FilterBase implements OnChanges {
    @Input() allowSameDaySelection = false;

    start: Date;
    end: Date;

    constructor() {
        super();
    }

    @Output() filterValueChanged = new EventEmitter<CompositeFilterDescriptor>();

    public get min(): Date | null {
        return this.start ? addDays(this.start, this.allowSameDaySelection ? 0 : 1) : null;
    }

    public get max(): Date | null {
        return this.end ? addDays(this.end, this.allowSameDaySelection ? 0 : -1) : null;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['filter']) {
            this.start = this.getLatestFilterValue(null, this.field + 'From');
            this.start = this.start ? new Date(this.start) : null;
            this.end = this.getLatestFilterValue(null, this.field + 'To');
            this.end = this.end ? new Date(this.end) : null;
        }
    }

    onStartChange(value: Date): void {
        this.filterRange(value, this.end);
    }

    onEndChange(value: Date): void {
        this.filterRange(this.start, value);
    }

    private filterRange(start: Date, end: Date): void {
        //CINT-1465: clear out any 'eq' operator date filters that may be been instantiated by Kendo - we only use lte/gte with date ranges
        this.removeFilter(this.field + 'From');
        this.removeFilter(this.field + 'To');

        if (this.allowSameDaySelection) {
            if (!end || start <= end) {
                this.updateFilter({
                    field: this.field + 'From',
                    operator: 'gte',
                    value: start
                });
                this.start = start;
            }
            if (!start || start <= end) {
                this.updateFilter({
                    field: this.field + 'To',
                    operator: 'lte',
                    value: end
                });
                this.end = end;
            }
        } else {
            if (!end || start < end) {
                this.updateFilter({
                    field: this.field + 'From',
                    operator: 'gte',
                    value: start
                });
                this.start = start;
            }
            if (!start || start < end) {
                this.updateFilter({
                    field: this.field + 'To',
                    operator: 'lte',
                    value: end
                });
                this.end = end;
            }
        }
        this.filterValueChanged.emit(this.filter);
    }
}
